<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  defineExpose,
  defineEmits,
  defineProps,
} from "vue";
// import { useLocalization } from "@/composables/useLocalization.ts";
import { useDropdownManager } from "@/composables/useDropdown.js";

const props = defineProps({
  modelValue: { type: [String, Array], default: "" },
  options: { type: Array, default: () => [] },
  width: { type: String, default: "215px" },
  height: { type: String, default: "46px" },
  arrowWidth: { type: String, default: "12" },
  label: { type: String, default: "" },
  arrowHeight: { type: String, default: "8" },
  displayValue: { type: String, default: "" },
  multiple: { type: Boolean, default: false },
  customPeriodKey: { type: String, default: "custom_period" },
});

const emit = defineEmits(["update:modelValue", "open"]);
// const { t, locale } = useLocalization();
const isOpen = ref(false);
const selectedOptions = ref(
  props.multiple ? [...props.modelValue] : props.modelValue
);

const dropdownRef = ref(null);
const generateId = () => Math.random().toString(36).substring(2, 15);
const dropdownId = ref(generateId());
const { registerActiveDropdown, clearActiveDropdown } = useDropdownManager();
const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    if (isOpen.value) {
      isOpen.value = false;
      clearActiveDropdown(dropdownId.value);
      emit("close");
    }
  }
};
// const openDropdown = () => {
//   const canOpen = registerActiveDropdown(dropdownId.value);
//   if (canOpen) {
//     isOpen.value = true;
//     emit("open");
//   }
// };
const toggleDropdown = () => {
  if (!isOpen.value) {
    // Если дропдаун закрыт, пытаемся его открыть
    const canOpen = registerActiveDropdown(dropdownId.value);
    if (canOpen) {
      isOpen.value = true;
      emit("open");
    }
  } else {
    // Если дропдаун открыт, закрываем его
    isOpen.value = false;
    clearActiveDropdown(dropdownId.value);
  }
};
const closeDropdown = () => {
  isOpen.value = false;
  clearActiveDropdown(dropdownId.value);
};
const selectOption = (option) => {
  if (props.multiple) {
    if (option.value === 'all') {
      selectedOptions.value = ['all'];
      isOpen.value = false;
      clearActiveDropdown(dropdownId.value);
      return emit('update:modelValue', ['all']);
    }

    selectedOptions.value = (selectedOptions.value || []).filter(v => v !== 'all');

    const idx = selectedOptions.value.indexOf(option.value);
    if (idx === -1) {
      selectedOptions.value.push(option.value);
    } else {
      selectedOptions.value.splice(idx, 1);
    }

    if (selectedOptions.value.length === 0) {
      selectedOptions.value = ['all'];
    }

    return emit('update:modelValue', [...selectedOptions.value]);
  } else {
    selectedOptions.value = option.value;
    isOpen.value = false;
    clearActiveDropdown(dropdownId.value);
    return emit('update:modelValue', option.value);
  }
};

// const handleDateChange = (dates) => {
//   dateRange.value = dates;
//   selectedOptions.value = props.customPeriodKey;
//   emit("update:modelValue", props.customPeriodKey);
//   emit("date-change", dates);
//   isOpen.value = false;

//   clearActiveDropdown(dropdownId.value);
// };

const displayText = computed(() => {
  if (props.multiple) {
    if (
      Array.isArray(selectedOptions.value) &&
      selectedOptions.value.length > 0
    ) {
      const selectedLabels = selectedOptions.value
        .map((value) => {
          const option = props.options.find((opt) => opt.value === value);
          return option ? option.label : value;
        })
        .filter(Boolean);

      // Ограничиваем длину отображаемого текста
      const joinedText = selectedLabels.join(", ");
      return joinedText.length > 30
        ? joinedText.substring(0, 30) + "..."
        : joinedText;
    }
    return "";
  } else {
    if (selectedOptions.value) {
      const option = props.options.find(
        (opt) => opt.value === selectedOptions.value
      );
      const text = option ? option.label : selectedOptions.value;
      // Ограничиваем длину для одиночного выбора тоже
      return text.length > 30 ? text.substring(0, 30) + "..." : text;
    }
    return "";
  }
});

const hasValue = computed(() => {
  return props.multiple
    ? selectedOptions.value.length > 0
    : !!selectedOptions.value;
});

const filteredOptions = computed(() => {
  return props.options.filter(
    (option) => option.value !== props.customPeriodKey
  );
});

// const hasCustomPeriodOption = computed(() => {
//   return props.options.some((option) => option.value === props.customPeriodKey);
// });

// const handleSelectDate = () => {
//   datepickerRef.value.selectDate();
//   isOpen.value = false;
//   clearActiveDropdown(dropdownId.value);
// };

// const handleCancelDate = () => {
//   datepickerRef.value.clearValue();
//   isOpen.value = false;
//   clearActiveDropdown(dropdownId.value);
//   emit("cancel-date");
// };

watch(
  () => props.modelValue,
  (newValue) => {
    // Существующий код для обновления selectedOptions
    if (props.multiple) {
      selectedOptions.value = Array.isArray(newValue) ? [...newValue] : [];
    } else {
      selectedOptions.value = newValue;

      // Если новое значение равно customPeriodKey, открываем выпадающий список
      if (newValue === props.customPeriodKey) {
        // Регистрируем выпадающий список как активный и открываем его
        const canOpen = registerActiveDropdown(dropdownId.value);
        if (canOpen) {
          isOpen.value = true;
          emit("open");
        }
      }
    }
  },
  { deep: true, immediate: true }
);
const forceUpdateSelectedOptions = () => {
  if (props.multiple) {
    selectedOptions.value = Array.isArray(props.modelValue)
      ? [...props.modelValue]
      : [];
  } else {
    selectedOptions.value = props.modelValue;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  clearActiveDropdown(dropdownId.value);
});
defineExpose({
  closeDropdown,
  forceUpdateSelectedOptions,
});
</script>

<template>
  <div
    class="custom-dropdown"
    :style="{ width: props.width }"
    ref="dropdownRef"
  >
    <div
      class="custom-dropdown__selected"
      @click="toggleDropdown"
      :style="{ height: props.height }"
      :class="{ 'has-value': hasValue }"
    >
      <label class="custom-dropdown__label">{{ props.label }}</label>
      <span class="custom-dropdown__text">{{ displayText }}</span>
      <span class="custom-dropdown__arrow">
        <svg
          :width="props.arrowWidth"
          :height="props.arrowHeight"
          viewBox="0 0 12 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.61726 0.803844C1.24729 0.433874 0.647448 0.433875 0.277477 0.803846C-0.0924928 1.17382 -0.0924924 1.77366 0.277478 2.14363L5.33012 7.19626C5.50778 7.37393 5.74875 7.47374 6.00001 7.47374C6.25127 7.47374 6.49223 7.37392 6.6699 7.19626L11.7225 2.14363C12.0925 1.77366 12.0925 1.17382 11.7225 0.803844C11.3526 0.433874 10.7527 0.433875 10.3827 0.803846L6.00001 5.18659L1.61726 0.803844Z"
            fill="#4D5460"
          />
        </svg>
      </span>
    </div>
    <div v-if="isOpen" class="custom-dropdown__options" @click.stop>
      <!-- Regular options -->
      <div
        v-for="option in filteredOptions"
        :key="option.value"
        class="custom-dropdown__option"
        :class="{
          'custom-dropdown__option--selected': multiple
            ? selectedOptions.includes(option.value)
            : option.value === selectedOptions,
        }"
        @click.stop="selectOption(option)"
      >
        {{ option.label }}
      </div>

      <!-- DatePicker instead of custom_period option -->
      <!-- <div
        class="custom-dropdown__option custom-period-option"
        v-if="hasCustomPeriodOption"
        :class="{
          'custom-dropdown__option--selected':
            selectedOptions === props.customPeriodKey,
        }"
      >
        <VueDatePicker
          ref="datepickerRef"
          v-model="dateRange"
          range
          position="left"
          :enable-time-picker="false"
          @update:modelValue="handleDateChange"
          :locale="calendar_lang"
          :cancel-text="$t('back')"
          :select-text="$t('select')"
          :teleport="true"
          :utc="false"
          :clearable="false"
          class="custom-dropdown__datepicker"
          :placeholder="t('your_interval')"
          :input-class-name="'custom-period-input'"
        >
          <template #dp-input>
            <div class="custom-period-display">
              {{ t("customPeriod") || "Свой период" }}
            </div>
          </template>

          <template #action-buttons>
            <div class="custom-buttons-container">
              <button class="custom-cancel-button" @click="handleCancelDate">
                {{ $t("back") }}
              </button>
              <button class="custom-select-button" @click="handleSelectDate">
                {{ $t("select") }}
              </button>
            </div>
          </template>
        </VueDatePicker>
      </div> -->
    </div>
  </div>
</template>

<style scoped lang="scss">
.custom-dropdown {
  position: relative;
  font-size: 16px;

  font-family: Open Sans, sans-serif;

  &__selected {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    background: #fff;
    border: 1px solid #dde5ed;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    color: #4d5460;
    position: relative;
    min-width: 0;

    &.has-value {
      .custom-dropdown__label {
        top: 0px;
        color: #4d5460;
        font-family: Open Sans, sans-serif;
        font-size: 10px;
      }
    }
  }

  &__label {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    transition: all 0.3s ease;
    color: #4d5460;
    background-color: #fff;
    padding: 0 4px;
  }

  &__text {
    flex-grow: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0; // Добавлено для правильной работы обрезания текста
    max-width: calc(100% - 24px); // Учитываем место для стрелки
  }

  &__arrow {
    margin-left: auto;
    flex-shrink: 0;
  }

  &__options {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background-color: #fff;
    border: 1px solid #dde5ed;
    border-radius: 12px;
    padding: 8px;
    max-height: 300px;
    overflow-y: auto;
    z-index: 10;
    box-shadow: 0px 0px 20px 0px #0000001a;
  }

  &__option {
    padding: 6px 8px;
    cursor: pointer;
    font-size: 14px;
    color: #4d5460;
    border-radius: 4px;
    margin-bottom: 4px;
    overflow: hidden; // Добавлено
    text-overflow: ellipsis; // Добавлено
    
    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      color: #005291;
      font-weight: 600;
      background-color: #f6f6f6;
    }

    &--selected {
      color: #005291;
      background-color: #f6f6f6;
      font-weight: 600;
    }
  }
}

.custom-buttons-container {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-inline-start: inherit !important;
  width: 100%;
}

.custom-cancel-button {
  color: red;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: Open Sans, sans-serif;
  font-size: 16px;
  padding: 12px 22px;
  border-radius: 6px;
  border: 1px solid #ffefef;
}

:deep(.dp__overlay) {
  display: none; /* Hide overlay since we're embedding in dropdown */
}
:deep(.dp__main) {
  .dp__input_wrap {
    position: relative;
  }

  /* Скрываем иконку календаря */
  .dp__input_icon {
    display: none;
  }

  /* Стилизуем календарь, когда он открыт */
  .dp__instance_calendar {
    margin-top: 8px;
  }
}
</style>
